/**
 * The company Audit log page's calls (R166, decision 927; docs/specs/cloud/audit-log.md;
 * 20261102150000_alert_devices_history_audit.sql): the company's own audit rows, newest first, and the kinds of
 * change and people found in them. The database lets only the company's tenant admins and managers read them.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { auditWho } from '@teslyn/domain';
import type { AuditRow } from '../admin/api.ts';

const fail = (e: { message: string } | null): void => {
  if (e) throw new Error(e.message);
};

export interface AuditQuery {
  /** The first part of the action ("project"); '' for every kind. */
  readonly family: string;
  /** One person's sign-in id; '' for everyone. */
  readonly actor: string;
  /** Words found in the action, target or detail; '' for none. */
  readonly search: string;
  readonly from: string | null;
  readonly to: string | null;
  /** The last row's id shown, for "Show older". */
  readonly before: number | null;
  readonly limit: number;
}

export async function loadCompanyAudit(
  sb: SupabaseClient,
  tenantId: string,
  q: AuditQuery,
): Promise<AuditRow[]> {
  const { data, error } = await sb.rpc('company_audit_log', {
    p_tenant: tenantId,
    p_family: q.family || null,
    p_actor: q.actor || null,
    p_search: q.search.trim() || null,
    p_from: q.from,
    p_to: q.to,
    p_before: q.before,
    p_limit: q.limit,
  });
  fail(error);
  return ((data ?? []) as Record<string, unknown>[]).map((r) => {
    const actor = {
      actor_name: String(r.actor_name ?? ''),
      actor_email: String(r.actor_email ?? ''),
      actor_kind: String(r.actor_kind ?? 'person'),
    };
    return {
      id: Number(r.id),
      at: String(r.at),
      tenantId,
      tenantName: '',
      actorEmail: actor.actor_email || null,
      actorName: auditWho(actor),
      action: String(r.action),
      target: String(r.target ?? ''),
      detail: r.detail,
    };
  });
}

export interface AuditPerson {
  readonly id: string;
  readonly label: string;
}

export interface AuditFilters {
  readonly families: readonly string[];
  readonly people: readonly AuditPerson[];
}

export async function loadCompanyAuditFilters(sb: SupabaseClient, tenantId: string): Promise<AuditFilters> {
  const { data, error } = await sb.rpc('company_audit_filters', { p_tenant: tenantId });
  fail(error);
  const o = (data ?? {}) as { families?: unknown; people?: unknown };
  const families = Array.isArray(o.families)
    ? o.families.filter((f): f is string => typeof f === 'string')
    : [];
  const people = (Array.isArray(o.people) ? o.people : []).flatMap((p): AuditPerson[] => {
    if (!p || typeof p !== 'object') return [];
    const r = p as Record<string, unknown>;
    if (typeof r.id !== 'string') return [];
    return [
      {
        id: r.id,
        label: auditWho({
          actor_name: String(r.name ?? ''),
          actor_email: String(r.email ?? ''),
          actor_kind: String(r.kind ?? 'person'),
        }),
      },
    ];
  });
  return { families, people };
}
